import React from 'react';
import { useAppSelector } from 'flow/hooks/useRedux';
import { selectOwnWaitingRoomStanding } from 'flow/selectors/video-selectors';
import { WaitingRoomStanding } from 'flow/utils/meeting-constants';
import LobbyButtonCancelWaiting from './LobbyButtonCancelWaiting';
import LobbyButtonJoin from './LobbyButtonJoin';
import { ViewerMeetingRole } from 'flow/hooks/useViewerMeetingRole';

interface Props {
    name: string;
    onJoinMeeting?: () => void;
    /** Asked once by MeetingLobby and handed down — see useViewerMeetingRole. */
    viewerMeetingRole: ViewerMeetingRole;
}

/**
 * The lobby's primary action, which is one of two things depending on whether
 * the door is still in front of us (VID-76).
 *
 * Held in the waiting room, the join has already been asked for and the answer
 * is out of our hands, so the only thing left to offer is the way out — while
 * the mic and camera controls over the video stay live, which is the whole
 * point of waiting from the lobby.
 *
 * Kept as a branch between two components rather than a branch inside one, so
 * the work behind joining does not run for someone who has already joined as
 * far as they are able to.
 */
const LobbyButtonPrimaryAction = ({
    name,
    onJoinMeeting,
    viewerMeetingRole,
}: Props) => {
    const ownWaitingRoomStanding = useAppSelector(selectOwnWaitingRoomStanding);

    if (ownWaitingRoomStanding === WaitingRoomStanding.WAITING) {
        return <LobbyButtonCancelWaiting />;
    }

    return (
        <LobbyButtonJoin
            name={name}
            onJoinMeeting={onJoinMeeting}
            viewerMeetingRole={viewerMeetingRole}
        />
    );
};

export default LobbyButtonPrimaryAction;
